import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@tsuyosa/auth';
import type { Schedule, Member, ChekiType, ChekiPricing } from '@tsuyosa/types';
import { getChekiSchedules, getScheduleCheki, getPublicMembers, createChekiOrder, getPaymentInfo } from '../lib/api';
import { StepBar }           from '../components/StepBar';
import { ScheduleCard }      from '../components/ScheduleCard';
import { ChekiTypeSelector } from '../components/ChekiTypeSelector';
import { MemberGrid }        from '../components/MemberGrid';
import { ReviewStep }        from '../components/ReviewStep';
import { PaymentStep }       from '../components/PaymentStep';
import { GuestBanner }       from '../components/GuestBanner';
import { LoginModal }        from '../components/LoginModal';

type StepNum = 1 | 2 | 3 | 4 | 5;

interface FormState {
  type:             ChekiType;
  memberQuantities: Record<string, number>;
  quantity:         number;
  buyer_name:       string;
  email:            string;
  contact:          string;
}

function formatPrice(n: number) {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency', currency: 'IDR', maximumFractionDigits: 0,
  }).format(n);
}

function formatDateShort(d: string) {
  return new Date(d + 'T00:00:00').toLocaleDateString('id-ID', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

const INPUT_CLASS =
  'w-full rounded-lg border px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none transition-colors';

export function ChekiPage() {
  const { user, accessToken, isLoading: authLoading } = useAuth();

  const [step, setStep]           = useState<StepNum>(1);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [showLogin, setShowLogin] = useState(false);

  const [schedules, setSchedules]               = useState<Schedule[]>([]);
  const [members, setMembers]                   = useState<Member[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [pricing, setPricing]                   = useState<ChekiPricing[]>([]);

  const [form, setForm] = useState<FormState>({
    type: '2shot_solo', memberQuantities: {}, quantity: 1,
    buyer_name: '', email: '', contact: '',
  });
  const [isSubmitting, setIsSubmitting]         = useState(false);
  const [submitError, setSubmitError]           = useState('');
  const [orderId, setOrderId]                   = useState('');
  const [orderToken, setOrderToken]             = useState('');
  const [deadlineAt, setDeadlineAt]             = useState<Date | null>(null);
  const [paymentImageUrl, setPaymentImageUrl]   = useState<string | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isScheduleLoading, setIsScheduleLoading] = useState(false);

  function goTo(next: StepNum, dir: 'forward' | 'back') {
    setDirection(dir);
    setStep(next);
  }

  const selectSchedule = useCallback(async (schedule: Schedule) => {
    setSelectedSchedule(schedule);
    setIsScheduleLoading(true);
    try {
      const chekiData = await getScheduleCheki(schedule.id);
      setPricing(chekiData.pricing);
      const firstType = chekiData.pricing[0]?.type;
      if (firstType) {
        setForm(prev => ({ ...prev, type: firstType, memberQuantities: {} }));
      }
    } catch {
      setPricing([]);
    }
    setIsScheduleLoading(false);
    goTo(2, 'forward');
  }, []);

  useEffect(() => {
    if (authLoading) return;
    const scheduleParam = new URLSearchParams(window.location.search).get('schedule');
    Promise.all([getChekiSchedules(), getPublicMembers()]).then(([s, m]) => {
      setSchedules(s);
      setMembers(m);
      if (scheduleParam) {
        const found = s.find(x => x.id === scheduleParam);
        if (found) { selectSchedule(found); return; }
      }
      setIsInitialLoading(false);
    }).catch(() => setIsInitialLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  useEffect(() => {
    if (user) {
      setForm(prev => ({
        ...prev,
        buyer_name: prev.buyer_name || (user.display_name ?? ''),
        email:      prev.email      || user.email,
      }));
    }
  }, [user]);

  async function handleCreateOrder() {
    setSubmitError('');
    setIsSubmitting(true);
    try {
      const result = await createChekiOrder({
        schedule_id: selectedSchedule!.id,
        buyer_name:  form.buyer_name,
        email:       form.email,
        contact:     form.contact || undefined,
        type:        form.type,
        ...(form.type === '2shot_solo'
          ? { items: Object.entries(form.memberQuantities).map(([member_id, quantity]) => ({ member_id, quantity })) }
          : { quantity: form.quantity }
        ),
      }, accessToken);
      setOrderId(result.id);
      setOrderToken(result.order_token);
      setDeadlineAt(new Date(result.payment_deadline_at));
      goTo(4, 'forward');
    } catch (err) {
      setSubmitError((err as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  }
  function resetForm() {
    setForm({
      type: '2shot_solo', memberQuantities: {}, quantity: 1,
      buyer_name: user?.display_name ?? '',
      email:      user?.email        ?? '',
      contact:    '',
    });
    setSelectedSchedule(null);
    setOrderId('');
    setOrderToken('');
    setDeadlineAt(null);
    setPaymentImageUrl(null);
    setSubmitError('');
    goTo(1, 'back');
  }

  useEffect(() => {
    if (step !== 4) return;
    getPaymentInfo().then(d => setPaymentImageUrl(d.image_url)).catch(() => {});
  }, [step]);

  const currentPricing = pricing.find(p => p.type === form.type);
  const totalQuantity  = form.type === '2shot_solo'
    ? Object.values(form.memberQuantities).reduce((a, b) => a + b, 0)
    : form.quantity;
  const totalPrice     = (currentPricing?.price_preorder ?? 0) * totalQuantity;
  const memberItems    = form.type === '2shot_solo'
    ? Object.entries(form.memberQuantities)
        .filter(([, qty]) => qty > 0)
        .map(([id, qty]) => ({ name: members.find(m => m.id === id)?.nickname ?? id, quantity: qty }))
    : undefined;
  const slideClass     = `animate-slide-${direction}`;

  // ─── Global loading ───────────────────────────────────────────────────────

  if (isInitialLoading || isScheduleLoading) {
    return (
      <>
        <StepBar current={step} />
        <div className="flex min-h-[calc(100vh-60px)] items-center justify-center">
          <div
            className="h-8 w-8 animate-spin rounded-full border-2 border-t-transparent"
            style={{ borderColor: 'var(--pink)' }}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}

      {/* Step bar — does NOT slide */}
      <StepBar current={step} />

      {/* ─── Step 1: Select Schedule ──────────────────────────────────────── */}

      {step === 1 && (
        <div key={1} className={`${slideClass} min-h-screen px-4 py-8`}>
          <div className="mx-auto max-w-md">
            <div className="mb-8">
              <p className="text-[10px] uppercase tracking-[0.4em] text-white/30">TSUYOSA</p>
              <h1 className="mt-1 text-2xl font-bold tracking-tight text-white">Pre-order Cheki</h1>
            </div>

            {!user && !authLoading && (
              <GuestBanner onLoginClick={() => setShowLogin(true)} />
            )}

            <p className="mb-3 text-[10px] uppercase tracking-[0.3em] text-white/40">Pilih Jadwal</p>

            {schedules.length === 0 ? (
              <div
                className="flex flex-col items-center gap-3 rounded-xl border py-12 text-center"
                style={{ borderColor: 'var(--border)' }}
              >
                <span className="text-3xl opacity-30">📷</span>
                <p className="text-sm text-white/30">Belum ada jadwal cheki yang tersedia.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {schedules.map(schedule => (
                  <ScheduleCard
                    key={schedule.id}
                    schedule={schedule}
                    onClick={() => selectSchedule(schedule)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Step 2: Order Form ───────────────────────────────────────────── */}

      {step === 2 && selectedSchedule && (
        <div key={2} className={`${slideClass} min-h-screen`}>
          {/* Schedule summary bar */}
          <div
            className="sticky top-[60px] z-40 flex items-center gap-3 border-b px-4 py-2.5"
            style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
          >
            <button
              onClick={() => goTo(1, 'back')}
              className="shrink-0 text-sm text-white/40 transition-colors hover:text-white"
            >
              ← Ganti
            </button>
            <span className="min-w-0 flex-1 truncate text-sm">
              <span className="text-white/70">{selectedSchedule.title}</span>
              <span className="text-white/30"> · {formatDateShort(selectedSchedule.date)}</span>
            </span>
          </div>

          <div className="px-4 py-6">
            <div className="mx-auto max-w-md">
              {!user && !authLoading && (
                <GuestBanner onLoginClick={() => setShowLogin(true)} />
              )}

              <form onSubmit={(e) => { e.preventDefault(); goTo(3, 'forward'); }} className="flex flex-col gap-5">
                <ChekiTypeSelector
                  pricing={pricing}
                  value={form.type}
                  onChange={t => setForm(prev => ({ ...prev, type: t, memberQuantities: {} }))}
                />

                {form.type === '2shot_solo' && (
                  <>
                    <MemberGrid
                      members={members}
                      value={form.memberQuantities}
                      onChange={mqs => setForm(prev => ({ ...prev, memberQuantities: mqs }))}
                    />
                    {totalQuantity > 0 && currentPricing && (
                      <p className="text-sm text-white/40">
                        Total: {formatPrice(totalPrice)} ({totalQuantity} lembar)
                      </p>
                    )}
                  </>
                )}

                {/* Quantity — only for all_member */}
                {form.type === 'all_member' && (
                  <div>
                    <p className="mb-2 text-[10px] uppercase tracking-[0.3em] text-white/40">Jumlah</p>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, quantity: Math.max(1, prev.quantity - 1) }))}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-white transition-colors hover:bg-white/5"
                        style={{ borderColor: 'var(--border)' }}
                      >−</button>
                      <span className="min-w-[2rem] text-center text-lg font-bold text-white">
                        {form.quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, quantity: prev.quantity + 1 }))}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-white transition-colors hover:bg-white/5"
                        style={{ borderColor: 'var(--border)' }}
                      >+</button>
                      {currentPricing && (
                        <span className="ml-1 text-sm text-white/40">
                          = {formatPrice(totalPrice)}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Buyer info */}
                <div
                  className="flex flex-col gap-4 rounded-xl border p-4"
                  style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
                >
                  <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Data Pemesan</p>

                  <div>
                    <div className="mb-1.5 flex items-center gap-2">
                      <label className="text-xs text-white/40">Nama</label>
                      {user && (
                        <span className="text-[9px]" style={{ color: 'rgba(244,114,182,0.55)' }}>
                          ✦ Dari akun Tsun
                        </span>
                      )}
                    </div>
                    <input
                      type="text" required
                      value={form.buyer_name}
                      onChange={e => setForm(prev => ({ ...prev, buyer_name: e.target.value }))}
                      placeholder="Nama lengkap"
                      className={INPUT_CLASS}
                      style={{ background: 'var(--surface-hi)', borderColor: 'var(--border)' }}
                      onFocus={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(244,114,182,0.4)'; }}
                      onBlur={e => { (e.target as HTMLInputElement).style.borderColor = 'var(--border)'; }}
                    />
                  </div>

                  <div>
                    <div className="mb-1.5 flex items-center gap-2">
                      <label className="text-xs text-white/40">Email</label>
                      {user && (
                        <span className="text-[9px]" style={{ color: 'rgba(244,114,182,0.55)' }}>
                          ✦ Dari akun Tsun
                        </span>
                      )}
                    </div>
                    <input
                      type="email" required
                      value={form.email}
                      onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="email@contoh.com"
                      className={INPUT_CLASS}
                      style={{ background: 'var(--surface-hi)', borderColor: 'var(--border)' }}
                      onFocus={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(244,114,182,0.4)'; }}
                      onBlur={e => { (e.target as HTMLInputElement).style.borderColor = 'var(--border)'; }}
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-xs text-white/40">
                      WhatsApp / Telegram
                      <span className="ml-1 text-white/20">(opsional)</span>
                    </label>
                    <input
                      type="text"
                      value={form.contact}
                      onChange={e => setForm(prev => ({ ...prev, contact: e.target.value }))}
                      placeholder="08xxxxxxxxxx"
                      className={INPUT_CLASS}
                      style={{ background: 'var(--surface-hi)', borderColor: 'var(--border)' }}
                      onFocus={e => { (e.target as HTMLInputElement).style.borderColor = 'rgba(244,114,182,0.4)'; }}
                      onBlur={e => { (e.target as HTMLInputElement).style.borderColor = 'var(--border)'; }}
                    />
                  </div>
                </div>

                {submitError && (
                  <p className="rounded-lg bg-red-500/10 px-3 py-2.5 text-sm text-red-400">
                    {submitError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || (form.type === '2shot_solo' && totalQuantity === 0)}
                  className="flex w-full items-center justify-center rounded-xl py-3.5 font-semibold text-white transition-opacity disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, var(--pink), var(--pink-dark))' }}
                >
                  {isSubmitting
                    ? <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    : 'Pesan Sekarang →'
                  }
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ─── Step 3: Review ──────────────────────────────────────────────── */}

      {step === 3 && selectedSchedule && (
        <div key={3} className={slideClass}>
          <ReviewStep
            schedule={{ title: selectedSchedule.title, date: selectedSchedule.date, venue: selectedSchedule.venue }}
            type={form.type}
            memberItems={memberItems}
            quantity={totalQuantity}
            totalPrice={totalPrice}
            buyerName={form.buyer_name}
            email={form.email}
            contact={form.contact}
            isLoading={isSubmitting}
            error={submitError}
            onBack={() => goTo(2, 'back')}
            onConfirm={handleCreateOrder}
          />
        </div>
      )}

      {/* ─── Step 4: Payment ─────────────────────────────────────────────── */}

      {step === 4 && deadlineAt && (
        <div key={4} className={slideClass}>
          <PaymentStep
            orderId={orderId}
            orderToken={orderToken}
            deadlineAt={deadlineAt}
            paymentImageUrl={paymentImageUrl}
            onSuccess={() => goTo(5, 'forward')}
          />
        </div>
      )}

      {/* ─── Step 5: Success ─────────────────────────────────────────────── */}

      {step === 5 && (
        <div
          key={5}
          className={`${slideClass} flex min-h-screen flex-col items-center justify-center px-4 py-12`}
        >
          <div className="mx-auto w-full max-w-sm text-center">
            <div className="mb-4 text-5xl">✓</div>
            <h2 className="mb-2 text-xl font-bold text-white">Bukti Bayar Diterima!</h2>
            <p className="mb-1 text-sm text-white/50">
              Status: <span className="text-amber-400">Menunggu Verifikasi</span>
            </p>
            <p className="mb-6 text-sm text-white/40">
              Cek email <strong className="text-white/60">{form.email}</strong> untuk link status pesananmu.
            </p>
            <a
              href={`/order/${orderId}?token=${encodeURIComponent(orderToken)}`}
              className="mb-6 block text-sm font-medium"
              style={{ color: 'var(--pink)' }}
            >
              Lihat Status Pesanan →
            </a>
            <button onClick={resetForm} className="text-sm text-white/30 transition hover:text-white/60">
              Pesan lagi
            </button>
          </div>
        </div>
      )}
    </>
  );
}
